import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation, // Added useLocation
} from "react-router-dom";
import { AnimatePresence } from "framer-motion"; // Added AnimatePresence

import Home from "./components/Home";
import AlbumList from "./components/AlbumList";
import AlbumDetails from "./components/AlbumDetails";
import PlayAlbum from "./components/PlayAlbum";
import ErrorPage from "./components/ErrorPage";
import { Analytics } from "@vercel/analytics/react";
import { useQueryClient } from "@tanstack/react-query";
import { getAlbums } from "./services/api";
import { Toaster } from "react-hot-toast";

/**
 * Navigation bar:
 * - Fixed top header
 * - Responsive design for mobile
 * - Matches dark theme palette
 */
function Nav() {
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-surface/95 backdrop-blur supports-[backdrop-filter]:bg-surface/75">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3 flex items-center justify-between">
        {/* Brand */}
        <Link
          to="/"
          className="font-black tracking-tight text-white text-lg sm:text-xl md:text-2xl"
        >
          RISHI'S<span className="text-accent">RECORDS</span>
        </Link>

        {/* Nav Buttons */}
        <nav className="flex items-center gap-2 sm:gap-3">
          {/* Albums Link (secondary button) */}
          <Link
            to="/albums"
            className="
              inline-flex items-center justify-center
              bg-gray-800
              hover:bg-gray-700
              text-gray-100 font-semibold
              text-xs sm:text-sm
              px-2.5 sm:px-4
              py-1.5 sm:py-2.5
              rounded-md
              border border-gray-700
              shadow-sm
              transition-all
              duration-150
              focus:outline-none focus:ring-2 focus:ring-sky-500
            "
          >
            Albums
          </Link>

          {/* Add Album Button (primary) */}
          <Link
            to="/add/1"
            className="
              inline-flex items-center justify-center
              bg-[#3b82f6]
              hover:bg-[#2563eb]
              text-white font-semibold
              text-xs sm:text-sm
              px-2.5 sm:px-4
              py-1.5 sm:py-2.5
              rounded-md
              border border-transparent
              shadow-sm
              transition-colors
              duration-150
              focus:outline-none focus:ring-2 focus:ring-sky-500
            "
          >
            Rate Album
          </Link>
        </nav>
      </div>
    </header>
  );
}

/**
 * AnimatedRoutes:
 * Handles the AnimatePresence logic which requires useLocation().
 */
function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Home />} />
        <Route path="/albums" element={<AlbumList />} />
        <Route path="/add/:numAlbums" element={<AlbumDetails />} />
        <Route path="/play/:id" element={<PlayAlbum />} />
        <Route path="*" element={<ErrorPage message="Page not found" />} />
      </Routes>
    </AnimatePresence>
  );
}

/**
 * App:
 * - Keeps all backend route logic
 * - Prefetches albums for performance
 * - Global Toaster for clean success/error messages
 */
export default function App() {
  const queryClient = useQueryClient();

  // Default to dark mode
  useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);

  // Prefetch albums when app mounts
  useEffect(() => {
    queryClient
      .prefetchQuery({
        queryKey: ["albums"],
        queryFn: getAlbums,
      })
      .catch((err) => console.error("Prefetch failed:", err));
  }, [queryClient]);

  return (
    <div className="min-h-screen bg-panel text-gray-200">
      <Router>
        <Nav />
        <main>
          <AnimatedRoutes />
        </main>
      </Router>

      {/* 🔔 Global Toaster for success/error feedback */}
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: "#111111",
            color: "#f9fafb",
            border: "1px solid #333",
            fontFamily: "Inter, system-ui, sans-serif",
          },
          success: {
            iconTheme: {
              primary: "#3b82f6",
              secondary: "#111",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "#111",
            },
          },
        }}
      />
      <Analytics />
    </div>
  );
}
